<template>
  <!--
    CoursesPanel — shared, presentational "Courses" container (ACC-355).
    Fits any width / any location. Drop independent cards into the tab slots
    (#tab-<value>) or the default slot; the panel owns the chrome only
    (title, add-course action, segmented pill toggle, scroll region).
    It does NOT know or care what a "course card" looks like.
  -->
  <section
    class="courses-panel"
    :class="{
      'border-dashboard': bordered,
      'courses-panel--card': variant === 'card',
      'courses-panel--rounded': rounded
    }"
  >
    <!-- Header: title + add-course action (with bottom divider) -->
    <div class="courses-panel__top">
      <header class="courses-panel__header">
        <slot name="title">
          <h2 class="courses-panel__title ma-0">{{ title }}</h2>
        </slot>

        <slot name="header-action">
          <template v-if="showAddButton">
            <!-- Full pill button; collapses to the round icon at the same
                 breakpoint the tabs collapse into the Filters pill. -->
            <v-btn
              v-if="!compact"
              variant="flat"
              rounded
              prepend-icon="mdi-plus"
              class="courses-panel__add-btn font-weight-bold flex-shrink-0"
              @click="onAdd"
            >
              {{ addButtonLabel }}
            </v-btn>
            <!-- Compact: round icon button -->
            <v-btn
              v-else
              icon="mdi-plus"
              variant="flat"
              size="small"
              :aria-label="addButtonLabel"
              class="courses-panel__add-btn courses-panel__add-btn--round mobile-touch-target-icon flex-shrink-0"
              @click="onAdd"
            />
          </template>
        </slot>
      </header>
    </div>

    <!-- Segmented pill toggle (desktop). On mobile it collapses into the
         "Filters" dropdown that sits on the subheader row below. -->
    <div v-if="tabs.length && !useFilterMenu" class="courses-panel__toggle-wrap">
      <div ref="toggle" class="courses-panel__toggle" role="tablist">
        <!-- Sliding active-pill indicator -->
        <span
          class="courses-panel__toggle-indicator"
          :class="{ 'courses-panel__toggle-indicator--ready': indicatorReady }"
          :style="indicatorStyle"
          aria-hidden="true"
        />
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          role="tab"
          :aria-selected="String(modelValue === tab.value)"
          class="courses-panel__toggle-btn mobile-touch-target"
          :class="{ 'courses-panel__toggle-btn--active': modelValue === tab.value }"
          @click="selectTab(tab.value)"
        >
          <span class="courses-panel__toggle-label">{{ tab.label }}</span>
          <span v-if="tab.badge" class="courses-panel__badge">{{ tab.badge }}</span>
        </button>
      </div>
    </div>

    <!-- Panel-level alert (transcript deadline banner). Figma 1123-89953 places
         it directly under the tabs, above the results count. -->
    <div v-if="$slots.banner" class="courses-panel__banner">
      <slot name="banner" />
    </div>

    <!-- Results-count subheader (between banner and cards, outside scroll).
         On mobile it also carries the "Filters" tab dropdown on the right. -->
    <div v-if="$slots.subheader || showFilterMenu" class="courses-panel__subheader">
      <div class="d-flex align-center ga-3">
        <span class="flex-grow-1"><slot name="subheader" :active-tab="modelValue" /></span>

        <v-menu v-if="showFilterMenu" location="bottom end" :offset="4">
          <template #activator="{ props }">
            <button
              v-bind="props"
              type="button"
              class="courses-panel__filters mobile-touch-target flex-shrink-0"
              aria-label="Filter courses"
            >
              <span class="courses-panel__filters-label">Filters</span>
              <v-icon size="24">mdi-filter-variant</v-icon>
            </button>
          </template>

          <v-list class="asu-menu" role="tablist">
            <v-list-item
              v-for="tab in tabs"
              :key="tab.value"
              role="tab"
              :aria-selected="String(modelValue === tab.value)"
              class="asu-menu__item"
              @click="selectTab(tab.value)"
            >
              <div class="d-flex align-center ga-2 w-100">
                <span class="asu-menu__label flex-grow-1">{{ tab.label }}</span>
                <span v-if="tab.badge" class="courses-panel__badge">{{ tab.badge }}</span>
                <v-icon size="24" :style="{ visibility: modelValue === tab.value ? 'visible' : 'hidden' }"
                  >mdi-check</v-icon
                >
              </div>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Content region: cards get dumped here -->
    <div
      class="courses-panel__content"
      :class="{ 'courses-panel__content--scroll': maxContentHeight != null }"
      :style="contentStyle"
    >
      <div class="d-flex flex-column ga-4">
        <slot :name="activeSlotName" :active-tab="modelValue">
          <slot :active-tab="modelValue" />
        </slot>
      </div>
    </div>
  </section>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'CoursesPanel',

  props: {
    /** Panel heading. */
    title: {
      type: String,
      default: 'Courses'
    },
    /** Segmented toggle options: [{ value, label, badge? }]. Empty = no toggle. */
    tabs: {
      type: Array,
      default: () => []
    },
    /** Active tab value (v-model). */
    modelValue: {
      type: String,
      default: ''
    },
    showAddButton: {
      type: Boolean,
      default: true
    },
    addButtonLabel: {
      type: String,
      default: 'Add course'
    },
    /** When set, the content region scrolls internally at this max height (px or any CSS length). */
    maxContentHeight: {
      type: [String, Number],
      default: null
    },
    /** Outer 1px container border. */
    bordered: {
      type: Boolean,
      default: true
    },
    /** 'default' = square edges, header divider (Accelerate / My Courses).
     *  'card' = rounded corners, no divider (main dashboard, Figma 1123-103851). */
    variant: {
      type: String,
      default: 'default'
    },
    /** Collapse the pill toggle into a "Filters" dropdown on small screens
     *  (Figma 1127-9000). Set false to always keep the pills. */
    collapseTabsOnMobile: {
      type: Boolean,
      default: true
    },
    /** Rounded outer corners without the rest of the card variant. My Courses
     *  wants rounded edges but keeps the header divider. */
    rounded: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue', 'add-course'],

  data() {
    return {
      indicatorStyle: { opacity: 0 },
      indicatorReady: false
    };
  },

  computed: {
    // Single breakpoint for every compact-header decision, so the tab pills and
    // the add-course label can't collapse at different widths (they used to:
    // tabs at xs, the button at smAndDown, leaving 600-960px inconsistent).
    compact() {
      return this.$vuetify.display.xs;
    },
    useFilterMenu() {
      return this.collapseTabsOnMobile && this.compact;
    },
    showFilterMenu() {
      return this.useFilterMenu && this.tabs.length > 0;
    },
    activeSlotName() {
      return this.modelValue ? `tab-${this.modelValue}` : 'default';
    },
    contentStyle() {
      if (this.maxContentHeight == null) return {};
      const h = typeof this.maxContentHeight === 'number' ? `${this.maxContentHeight}px` : this.maxContentHeight;
      return { maxHeight: h };
    }
  },

  watch: {
    modelValue() {
      this.$nextTick(this.updateIndicator);
    },
    tabs: {
      deep: true,
      handler() {
        this.$nextTick(this.updateIndicator);
      }
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.updateIndicator();
      // Enable the slide transition only after the first position is set,
      // so the pill doesn't animate in from the corner on load.
      requestAnimationFrame(() => {
        this.indicatorReady = true;
      });
    });
    window.addEventListener('resize', this.updateIndicator);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateIndicator);
  },

  methods: {
    updateIndicator() {
      const track = this.$refs.toggle;
      if (!track) return;
      const idx = this.tabs.findIndex((t) => t.value === this.modelValue);
      const btn = track.querySelectorAll('.courses-panel__toggle-btn')[idx];
      if (!btn) {
        this.indicatorStyle = { opacity: 0 };
        return;
      }
      this.indicatorStyle = {
        width: `${btn.offsetWidth}px`,
        height: `${btn.offsetHeight}px`,
        transform: `translate(${btn.offsetLeft}px, ${btn.offsetTop}px)`,
        opacity: 1
      };
    },
    selectTab(value) {
      if (value !== this.modelValue) this.$emit('update:modelValue', value);
    },
    onAdd() {
      this.$emit('add-course');
    }
  }
});
</script>

<style lang="scss" scoped>
.courses-panel {
  background: var(--asu-white, #fff);
  display: flex;
  flex-direction: column;
  width: 100%;

  // Vertical rhythm from Figma 1123-89953/103851: 24px outer padding, a uniform
  // 16px between every block (tabs, banner, results count, content). Each block
  // owns its top padding so gaps stay correct when one is absent.
  &__top {
    padding: 24px 24px 0;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 21px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--asu-grayscale-gray-4, #bfbfbf);
  }

  // Rounded-card variant (main dashboard, Figma 1123-103851): rounded corners,
  // no header divider, bold 24px title. Border color stays #d0d0d0 (border-dashboard).
  &--card,
  &--rounded {
    border-radius: 12px;
  }
  &--card &__header {
    padding-bottom: 0;
    border-bottom: none;
  }

  &__toggle-wrap {
    padding: 16px 24px 0;
  }

  &__banner {
    padding: 16px 24px 0;
  }

  // Mobile "Filters" chip that replaces the pill toggle (Figma 1127-9000)
  &__filters {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 4px 12px;
    background: var(--asu-white, #fff);
    border: 1px solid var(--asu-grayscale-gray-1, #191919);
    border-radius: 100px;
    cursor: pointer;
    color: var(--asu-grayscale-gray-1, #191919);
  }

  &__filters-label {
    font-family: Arial, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: -0.48px;
    text-decoration: underline;
  }

  // Figma 1123-103851: regular weight at gray-3 — it is a quiet result count,
  // not a heading. The per-type heading below it carries the emphasis.
  &__subheader {
    padding: 16px 24px 0;
    font-family: Arial, sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: -0.48px;
    color: var(--asu-grayscale-gray-3, #747474);
  }

  // Bold in every variant so it reads as a section heading like "Pathways"
  // (Figma 1307-9645: 24/28 bold Arial).
  &__title {
    font-family: Arial, sans-serif;
    font-size: 24px;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: -0.72px;
    color: var(--asu-text-colors-primary-text, #191919);
  }

  // Figma 1123:104054 — 31px tall gold pill, 14px bold Arial, 14px icon at 4px
  // gap. v-btn's own height/typography defaults are pinned here rather than
  // left to ambient global CSS, so the panel renders the same wherever it is
  // mounted (app, Storybook, tests).
  &__add-btn {
    background-color: var(--asu-primary-colors-asu-gold, #ffc627) !important;
    color: var(--asu-text-colors-primary-text, #191919) !important;
    height: 31px !important;
    min-width: 0;
    padding: 0 16px !important;
    border-radius: 25px;
    font-family: Arial, sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: normal;
    text-transform: none;
    letter-spacing: normal;

    :deep(.v-btn__prepend) {
      margin-inline: 0 4px;
    }

    :deep(.v-icon) {
      font-size: 14px;
    }

    &--round {
      width: 30px;
      height: 30px !important;
      padding: 0 !important;
      border-radius: 50% !important;
    }
  }

  // Segmented pill toggle (Figma: gray track, white active pill)
  &__toggle {
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    width: 100%;
    background: var(--asu-grayscale-gray-6, #e8e8e8);
    border-radius: 100px;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  // The white pill that slides to the active tab
  &__toggle-indicator {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 0;
    border-radius: 100px;
    background: var(--asu-white, #fff);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    pointer-events: none;

    &--ready {
      transition:
        transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
        width 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }

  &__toggle-btn {
    position: relative;
    z-index: 1;
    flex: 1 1 0;
    min-width: max-content;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 4px 12px;
    background: transparent;
    border: none;
    border-radius: 100px;
    cursor: pointer;
    font-family: Arial, sans-serif;
    font-size: 16px;
    line-height: 24px;
    color: #484848;
    transition: color 0.2s ease;

    &:hover:not(&--active) {
      color: var(--asu-text-colors-primary-text, #191919);
    }

    &--active {
      color: var(--asu-text-colors-primary-text, #191919);
      font-weight: 700;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &__toggle-indicator--ready {
      transition: none;
    }
  }

  &__toggle-label {
    white-space: nowrap;
  }

  &__content {
    padding: 16px 24px 24px;

    &--scroll {
      overflow-y: auto;
      overflow-x: hidden;

      // Rounded scrollbar thumb to match Figma
      scrollbar-width: thin;
      scrollbar-color: var(--asu-grayscale-gray-4, #bfbfbf) transparent;

      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-thumb {
        background: var(--asu-grayscale-gray-4, #bfbfbf);
        border-radius: 100px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }
    }
  }
}

// Shared by the toggle pill and the teleported mobile menu, so it must not be
// nested under .courses-panel (the menu is not a DOM descendant of the panel).
.courses-panel__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 100px;
  background: var(--system-colors-error, #cc2f2f);
  color: #fff;
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
}

// Mobile tab dropdown surface comes from the shared .asu-menu classes
// (src/styles/menu.css) so it matches the card's Options menu.
</style>
